/**
 * The staged bed, in the BED'S OWN FRAME: what goes on it, what goes on the
 * nightstands beside it, and how big the art over it is.
 *
 * One copy for both callers — the debug page's synthetic bedroom
 * (`./vignette.js`) and the planner that stages a real one
 * (`../plan/bedroom/`). The planner transforms these positions to the room by
 * the scanned bed's `center` / `rotation`; the page shifts them along its own
 * room. Rules and their sources: claude-docs/2026-10-26-bedroom-staging-research.md.
 *
 * The frame: x across the bed, z along it with the HEAD at -z (66 of 76
 * corpus beds stand head-to-wall and every one is authored head at local -z),
 * y up from the FLOOR. Metres. Every position is a box CENTRE.
 *
 * Every prop box comes from the registry (`STAGING_PROP_DIMS` and its
 * variants); the only sizes computed here are the ones the rules derive from
 * the bed — the art's width (2/3 of the bed) and the duvet's (the mattress).
 */

const { STAGING_PROP_DIMS, STAGING_PROP_VARIANT_DIMS } = require('../propDims');
const { bedProportions } = require('./bedProportions');
const { stagedBedSurfaces } = require('./stagedBed');
const {
  pillowDepthM,
  pillowFaces,
  ownPillowShape,
  SOFA_PILLOW_SHAPE,
  BED_BACK_ROW_SHAPE,
} = require('./pillowTuning');
const {
  seededRandom,
  hashStringToSeed,
} = require('../../geometry/seededRandom');

// Bound once: a global `Math` lookup is not inline-cacheable inside jest's
// `vm` context, which made these per-point loops ~10x slower under test.
const MATH_ABS = Math.abs;
const MATH_COS = Math.cos;
const MATH_MAX = Math.max;
const MATH_MIN = Math.min;
const MATH_ROUND = Math.round;
const MATH_SIN = Math.sin;

const IN = 0.0254;
const boxM = (d) => ({ x: d.x * IN, y: d.y * IN, z: d.z * IN });
const propBox = (type, variant) =>
  boxM(
    (variant && STAGING_PROP_VARIANT_DIMS[type]?.[variant]) ||
      STAGING_PROP_DIMS[type]
  );

/**
 * Mattress sizes, W x L inches (RTINGS / Nectar), and what each gets.
 *
 * `paired`: a bed two people sleep in gets the symmetric treatment — a
 * nightstand, a lamp and a print on EACH side. A twin or a full sleeps one,
 * and a matched pair either side of a single sleeper reads as a hotel room
 * doubled for no one, so it gets one nightstand, one lamp and one print
 * (Megan's review, 2026-10-26).
 */
const BED_SIZES = {
  twin: {
    label: 'Twin',
    w: 38,
    l: 75,
    euros: 1,
    shams: 1,
    sham: null,
    paired: false,
  },
  full: {
    label: 'Full',
    w: 54,
    l: 75,
    euros: 2,
    shams: 2,
    sham: null,
    paired: false,
  },
  queen: {
    label: 'Queen',
    w: 60,
    l: 80,
    euros: 2,
    shams: 2,
    sham: 'queen',
    paired: true,
  },
  king: {
    label: 'King',
    w: 76,
    l: 80,
    euros: 3,
    shams: 2,
    sham: 'king',
    paired: true,
  },
  'cal-king': {
    label: 'Cal king',
    w: 72,
    l: 84,
    euros: 3,
    shams: 2,
    sham: 'king',
    paired: true,
  },
};

/**
 * What a scanned bed's box adds round its mattress. The corpus median bed is
 * 66 x 85 in around a queen's 60 x 80, so ~4-6 in.
 */
const FRAME_W_IN = 4;
const FRAME_L_IN = 5;

/**
 * The mattress class a scanned bed's box implies: the size whose mattress
 * plus frame is nearest its WIDTH, the dimension the kit's counts depend on.
 * A king-wide bed is a cal king when its length is nearer a cal king's.
 * A choice, not a source: RoomPlan measures the frame, not the mattress.
 */
function bedSizeClass(widthM, lengthM) {
  const wIn = widthM / IN - FRAME_W_IN;
  const lIn = lengthM / IN - FRAME_L_IN;
  const widths = ['twin', 'full', 'queen', 'king'];
  let best = widths[0];
  for (const s of widths) {
    if (MATH_ABS(BED_SIZES[s].w - wIn) < MATH_ABS(BED_SIZES[best].w - wIn)) {
      best = s;
    }
  }
  if (
    best === 'king' &&
    MATH_ABS(lIn - BED_SIZES['cal-king'].l) < MATH_ABS(lIn - BED_SIZES.king.l)
  ) {
    return 'cal-king';
  }
  return best;
}

/**
 * A table lamp's base, the part that must stand on the nightstand: the
 * shade above it may overhang. A choice — retailer pages give the shade and
 * the height, rarely the base; 7 in is a typical turned or ceramic foot.
 */
const LAMP_BASE_IN = 7;

/**
 * The least wall left above the art: ~20 cm / 7.8 in (TLC Interiors), "at
 * least seven to eight inches" between frame and ceiling.
 */
const ART_CEILING_GAP_IN = 8;
/** Art's bottom above the higher of headboard and pillows: 6-8 in. */
const ART_ABOVE_IN = 7;
/**
 * The narrowest print worth hanging over a bed: an 8 x 10, the smallest
 * standard size frames are sold in for a wall. A choice — below it a print
 * reads as a postcard over a bed. Under a 7 ft ceiling a 52 in headboard
 * leaves room for prints just over this.
 */
const MIN_PRINT_IN = 10;

/**
 * The companion print for a pair: a related subject in the same register, so
 * the two read as one series. Symmetric, and every subject offered for a
 * bedroom has one.
 */
const ART_COMPANIONS = {
  'minimal-arch': 'sun-arch',
  'sun-arch': 'minimal-arch',
  'single-line-figure': 'neutral-color-block',
  'neutral-color-block': 'single-line-figure',
  'botanical-plate': 'pressed-flower',
  'pressed-flower': 'botanical-plate',
  'wave-line': 'shell-study',
  'shell-study': 'wave-line',
  'topo-lines': 'pine-silhouette',
  'pine-silhouette': 'topo-lines',
};
const artCompanion = (motif) => ART_COMPANIONS[motif] || motif;

/** Bed sizes whose variant names differ from their key. */
const rugVariant = (size) =>
  size === 'twin'
    ? 'twin'
    : size === 'king' || size === 'cal-king'
      ? 'king'
      : null;
const throwVariant = (size) => (size === 'queen' ? null : size);
const duvetVariant = (size) => (size === 'queen' ? null : size);

/** Centres for `n` items of width `w` across `span`, overlapping if needed. */
function rowXs(n, w, span) {
  const step = MATH_MIN(w * 0.97, span / n);
  return Array.from({ length: n }, (_, i) => (i - (n - 1) / 2) * step);
}

/**
 * The bed and its bedding, centred at x = z = 0.
 *
 * @param {object} o
 * @param {{x:number,y:number,z:number}} o.bedBox  the bed's box in metres
 * @param {keyof BED_SIZES} o.size  picks the pillow counts and the pairing
 * @param {'staged'|'app'} [o.bedding]  'staged' dresses the bed in the staged
 *   duvet (the bed drawn bare); 'app' keeps the app bed's own duvet and seats
 *   the kit on it.
 * @param {{pillows?:boolean, throw?:boolean}} o.show
 * @param {object} o.styleOptions  renderer options for the style pack
 * @param {'squares'|'lumbar'|'both'} [o.frontRow]
 * @param {number} [o.looseness]  0 squares every pillow to the bed; 1 is the
 *   hand-placed look.
 * @returns {{ items: object[], bp: object, pillowTop: number, topY: number,
 *   mattress: {w:number,l:number}, headZ: number, footZ: number }}
 */
function dressStagedBed({
  bedBox: bed,
  size,
  bedding = 'staged',
  show,
  styleOptions,
  frontRow = 'squares',
  looseness = 1,
}) {
  const spec = BED_SIZES[size];
  const items = [];
  const add = (key, type, dims, position, extra = {}) =>
    items.push({ key, type, dims, position, rotationY: 0, ...extra });

  const headboard = styleOptions.bedding?.headboard;
  const bp = bedProportions(
    { width: bed.x, height: bed.y, depth: bed.z },
    headboard?.kind
  );
  // The built-in bed's own mattress: in front of its headboard, which stands
  // inside the scanned box (`bedProportions`).
  const mattress = { w: bed.x - 0.05, l: bp.mattressD };
  const headZ = bp.mattressZ - mattress.l / 2;
  const footZ = bp.mattressZ + mattress.l / 2;
  const topY = bp.mattressTop;
  // Staged, the bed comes bare and the kit dresses it; in 'app' mode its own
  // duvet stays and only its pillows give way to the kit's.
  add('bed', 'bed', bed, [0, bed.y / 2, 0], {
    furniture: true,
    options: { bare: bedding === 'staged', headboard },
    hideParts:
      bedding !== 'staged' && show.pillows
        ? ['bed-pillow-left', 'bed-pillow-right']
        : [],
  });

  // ── what the kit sits on ─────────────────────────────────────────────
  const s = stagedBedSurfaces(topY);
  const appDuvetTop = topY + 0.02 + bp.duvetH / 2;
  const headTop = bedding === 'staged' ? s.sheetTop : topY;
  const foldZ = headZ + s.foldFromHead;
  /** Bedding top under a footprint that reaches `frontZ` along the bed. */
  const restOn = (frontZ) =>
    bedding === 'staged' && frontZ > foldZ ? s.turnTop : headTop;

  if (bedding === 'staged') {
    const d = propBox('staging-duvet', duvetVariant(size));
    // Boxed to the mattress: the box floor IS the mattress top.
    add('duvet', 'staging-duvet', { ...d, x: mattress.w, z: mattress.l }, [
      0,
      topY + d.y / 2,
      bp.mattressZ,
    ]);
  }

  // ── pillows: euros, then shams, then the front row ───────────────────
  // Every reference bed is hand-placed: each pillow a few degrees off square,
  // the front pair tipped in toward each other. Rows standing dead straight
  // are what made the first version look "too perfect". Seeded per pillow,
  // so a bed keeps its arrangement.
  const jitterFor = (key) => {
    const rand = seededRandom(hashStringToSeed(key));
    const r = () => (rand() * 2 - 1) * looseness;
    return { yaw: r() * 0.07, roll: r() * 0.045, dx: r() * 0.6 * IN };
  };

  let pillowTop = headTop;
  if (show.pillows) {
    // The bed's pillows look like the sofa's (Megan, 2026-10-29): the
    // decorative front row takes its fuller shape, the euros and shams its
    // pointed corners (`BED_BACK_ROW_SHAPE`) — all under any tuning the page
    // hands in.
    const page = styleOptions.pillowTuning;
    const tuning = { ...page };
    Object.entries({ ...SOFA_PILLOW_SHAPE, ...BED_BACK_ROW_SHAPE }).forEach(
      ([k, shape]) => {
        tuning[k] = { ...shape, ...(page?.[k] || {}) };
      }
    );

    /**
     * Each pillow LEANS on what is behind it: pushed back until its back face
     * (`pillowFaces`, the shape the renderer draws) touches the headboard's
     * face, below the headboard's top, or the front of a pillow already
     * placed. Placing by the box instead stood the euros short of the
     * headboard — a lens is thinner than its box everywhere but the middle
     * (Megan, 2026-10-29: "floating upright"). Placed pillows' fronts are
     * kept, the furthest forward in each (across, height) cell, for the next
     * row; the living room seats a sofa's pillows the same way.
     */
    const hbFront = bp.hbZ + bp.hbThick / 2;
    const CELL = 1 * IN;
    const fronts = new Map();
    const cell = (x, y) => `${MATH_ROUND(x / CELL)},${MATH_ROUND(y / CELL)}`;
    const supportAt = (x, y) => {
      const f = fronts.get(cell(x, y));
      const hb = y <= bp.hbTop ? hbFront : -Infinity;
      return f == null ? hb : MATH_MAX(hb, f);
    };
    /** A face point in the bed frame, the pillow turned (cos, sin of its yaw) about its centre. */
    const toBed = ([px, py, pz], [cx, cy, cz], cos, sin) => [
      cx + px * cos + pz * sin,
      cy + py,
      cz - px * sin + pz * cos,
    ];
    /** The centre z at which the back face just touches its support. */
    const seatZ = (faces, cx, cy, yaw) => {
      let z = -Infinity;
      const cos = MATH_COS(yaw);
      const sin = MATH_SIN(yaw);
      for (const p of faces.back) {
        const [x, y, dz] = toBed(p, [cx, cy, 0], cos, sin);
        z = MATH_MAX(z, supportAt(x, y) - dz);
      }
      return z;
    };
    const keepFront = (faces, centre, yaw) => {
      const cos = MATH_COS(yaw);
      const sin = MATH_SIN(yaw);
      for (const p of faces.front) {
        const [x, y, z] = toBed(p, centre, cos, sin);
        const k = cell(x, y);
        if (!(fronts.get(k) >= z)) fronts.set(k, z);
      }
    };

    /**
     * A row of pillows, each its own shape (`ownPillowShape`, carried on the
     * record so the app draws the pillow the layout seated) and its box
     * deepened for its own lean and fill. A pillow whose front reaches past
     * the duvet's fold sits on the turned-back duvet (`restOn`).
     */
    const row = (kind, type, plain, places) => {
      const made = places.map((p) => {
        const own = ownPillowShape(kind, p.key, looseness, tuning);
        const pillowTuning = { [kind]: own };
        const box = {
          ...plain,
          z: pillowDepthM(kind, plain.y, pillowTuning),
        };
        const faces = pillowFaces(kind, box, pillowTuning, 31);
        const j = jitterFor(p.key);
        const x = p.x + j.dx;
        const yaw = (p.pose?.yaw || 0) + j.yaw;
        const seat = (base) => {
          const cy = base + box.y / 2;
          return { cy, cz: seatZ(faces, x, cy, yaw) };
        };
        let at = seat(headTop);
        if (restOn(at.cz + box.z / 2) !== headTop)
          at = seat(restOn(at.cz + box.z / 2));
        return { ...p, box, faces, x, yaw, ...at, pillowTuning, j };
      });
      // A row stands together: its fronts join the support only once all
      // of it is seated, so a pillow never leans on its neighbour.
      made.forEach((m) => {
        add(m.key, type, m.box, [m.x, m.cy, m.cz], {
          rotation: [0, m.yaw, (m.pose?.roll || 0) + m.j.roll],
          options: { pillowTuning: m.pillowTuning },
        });
        keepFront(m.faces, [m.x, m.cy, m.cz], m.yaw);
      });
    };

    const euro = propBox('staging-euro-sham');
    row(
      'euro',
      'staging-euro-sham',
      euro,
      rowXs(spec.euros, euro.x, mattress.w).map((x, i) => ({
        key: `euro-${i}`,
        x,
      }))
    );
    pillowTop = headTop + euro.y;

    const sham = propBox('staging-sham', spec.sham);
    row(
      'sham',
      'staging-sham',
      sham,
      rowXs(spec.shams, sham.x, mattress.w).map((x, i) => ({
        key: `sham-${i}`,
        x,
      }))
    );

    if (frontRow !== 'lumbar') {
      // The square pair, overlapping a little and angled in toward each
      // other; a twin's single sleeper gets one, square to the bed.
      const sq = propBox('staging-accent-pillow');
      const xs = spec.paired || size === 'full' ? [-1, 1] : [0];
      row(
        'accent',
        'staging-accent-pillow',
        sq,
        xs.map((side, i) => ({
          key: `accent-${i}`,
          x: side * sq.x * 0.46,
          pose: { yaw: -side * 0.17, roll: side * 0.05 },
        }))
      );
    }
    if (frontRow !== 'squares') {
      row('lumbar', 'staging-lumbar', propBox('staging-lumbar'), [
        { key: 'lumbar', x: 0 },
      ]);
    }
  }

  // ── the throw across the foot ────────────────────────────────────────
  if (show.throw) {
    const t = propBox('staging-bed-throw', throwVariant(size));
    // Foot edge 4 in in from the end of the mattress (no source gives a
    // figure; 0-6 in is the derived range).
    const cz = footZ - 4 * IN - t.z / 2;
    if (bedding === 'staged') {
      // On the staged duvet the throw is built against the duvet itself (its
      // roll and its folds), so it needs the bed, not a resting height.
      const cy = s.duvetTop + 1.2 * IN - t.y / 2;
      add('throw', 'staging-bed-throw', t, [0, cy, cz], {
        options: {
          onBed: {
            mattressW: mattress.w,
            mattressD: mattress.l,
            topY: topY - cy,
            zShift: bp.mattressZ - cz,
          },
        },
      });
    } else {
      const bandTop = appDuvetTop + 1.2 * IN;
      add('throw', 'staging-bed-throw', t, [0, bandTop - t.y / 2, cz], {
        options: { overWidth: bed.x + 0.03 },
      });
    }
  }

  return { items, bp, pillowTop, topY, mattress, headZ, footZ };
}

/**
 * The art over the bed: how many prints, how big, and where along the bed.
 *
 * A matched PAIR of portrait prints. The art library draws 3:4 portrait
 * images, and one print wide enough for the bed (2/3 of its width) would have
 * to be landscape — stretching a portrait image across it is what the first
 * version did. A pair is the other arrangement the sources give (Kuriosis).
 * Together they span 2/3 of the bed with a 3 in gap; the bottom sits 7 in
 * (6-8) above the higher of headboard and pillows (Fine Art America). A
 * single sleeper's bed gets ONE print, as wide as fits 2/3 of the bed under
 * a 30 in height.
 *
 * Under a low ceiling the prints shrink, at their 3:4, rather than move: the
 * gap above the pillows is the rule that places them, and 8 in of wall stays
 * above them (`ART_CEILING_GAP_IN`).
 *
 * @param {object} o
 * @param {number} o.bedWidth  metres
 * @param {boolean} o.paired
 * @param {number} o.above  floor-relative top of what the art clears (the
 *   higher of headboard and pillows)
 * @param {number} o.wallTop  floor-relative top of the wall face behind it
 * @param {{artMotif?:string, artSeed?:number}} [o.art]  the pack's recipe
 * @param {number} [o.maxWidth]  the clear wall over the bed, metres
 * @returns {{ bottom:number, w:number, h:number, gap:number,
 *   pieces: {x:number, artMotif:string, artSeed:number}[] } | null}  null
 *   when the wall leaves no room at all.
 */
function stagedArtForBed({
  bedWidth,
  paired,
  above,
  wallTop,
  art = {},
  maxWidth = Infinity,
}) {
  const gap = 3 * IN;
  let count = paired ? 2 : 1;
  const span = (bedWidth * 2) / 3;
  let w = (span - gap * (count - 1)) / count;
  let h = (w * 4) / 3;
  const maxH = (paired ? 36 : 30) * IN;
  if (h > maxH) {
    h = maxH;
    w = (h * 3) / 4;
  }
  const bottom = above + ART_ABOVE_IN * IN;
  const roomForArt = wallTop - ART_CEILING_GAP_IN * IN - bottom;
  if (h > roomForArt) {
    h = roomForArt;
    w = (h * 3) / 4;
  }
  // Between two windows, or a window and a corner, the clear wall over the
  // bed can be narrower than the group. The prints narrow to fit, at their
  // 3:4; a pair that would fall under `MIN_PRINT_IN` each becomes one print.
  const total = () => w * count + gap * (count - 1);
  if (total() > maxWidth) {
    w = (maxWidth - gap * (count - 1)) / count;
    if (count === 2 && w < MIN_PRINT_IN * IN) {
      count = 1;
      w = MATH_MIN(maxWidth, (h * 3) / 4);
    }
    h = MATH_MIN(h, (w * 4) / 3);
    w = (h * 3) / 4;
  }
  if (!(h > 0) || w < MIN_PRINT_IN * IN) return null;
  // A pair is two COMPANION prints from one series — same frame, same
  // palette, related subjects — not the same picture twice.
  const motifs = [art.artMotif, artCompanion(art.artMotif)];
  const pieces = Array.from({ length: count }, (_, i) => ({
    x: count === 1 ? 0 : (i === 0 ? -1 : 1) * (w / 2 + gap / 2),
    artMotif: motifs[i],
    artSeed: (art.artSeed ?? 1) + i,
  }));
  return { bottom, w, h, gap, pieces };
}

/**
 * What stands on one nightstand: the lamp at its OUTER BACK corner, then
 * books with the bud vase standing on them — or a small plant — at the INNER
 * FRONT corner, clear of the lamp shade's footprint. Three items of varying
 * height, everything else relaxed (Domino); the lamps a matched pair (Ideal
 * Home: 1-1.5x the nightstand).
 *
 * The box is the nightstand top in the bed frame: `outer` / `inner` are its
 * x edges away from / toward the bed, `back` / `front` its z edges toward the
 * head wall / the room, `top` its floor-relative height.
 *
 * Nothing is placed that does not fit: a lamp needs the box, and the low item
 * needs what is left beside the lamp's shade.
 *
 * @param {{outer:number, inner:number, back:number, front:number, top:number}} box
 * @param {-1|1} side  which side of the bed (the sign of x)
 * @param {{lamp:boolean, low: 'books'|'plant'|null, seed?: number}} wants
 * @returns {object[]} items, keyed `lamp-<side>`, `books-<side>`, ...
 */
function dressNightstand(box, side, wants) {
  const items = [];
  const add = (key, type, dims, position, extra = {}) =>
    items.push({ key, type, dims, position, rotationY: 0, ...extra });
  const width = MATH_ABS(box.outer - box.inner);
  const depth = box.front - box.back;
  const lamp = propBox('staging-table-lamp');
  let lampW = 0;
  // The lamp needs its BASE on the top; its shade may overhang a small
  // nightstand, as a real one does. Where the top is wide and deep enough
  // the shade sits flush with the outer edge and half an inch off the back;
  // where not, the lamp stands at the top's middle on that axis.
  const base = LAMP_BASE_IN * IN + 2 * IN;
  if (wants.lamp && width >= base && depth >= base) {
    const x =
      width >= lamp.x
        ? box.outer - side * (lamp.x / 2)
        : (box.outer + box.inner) / 2;
    const z =
      depth >= lamp.z + 0.5 * IN
        ? box.back + lamp.z / 2 + 0.5 * IN
        : (box.back + box.front) / 2;
    add(`lamp-${side}`, 'staging-table-lamp', lamp, [
      x,
      box.top + lamp.y / 2,
      z,
    ]);
    lampW = MATH_MIN(lamp.x, width);
  }
  const innerX = (w) => box.inner + side * (w / 2 + 1 * IN);
  const frontZ = (d) => box.front - d / 2 - 1 * IN;
  // The low item tucks UNDER the shade at the front, so it has to clear the
  // lamp's base, not its shade: an inch to the edges, and the half of the top
  // the lamp stands on. On the page's 22 in nightstand that leaves 15 in for
  // a 10 in stack; a 16 in one keeps its lamp and loses the books.
  const fits = (w, d) => w + 2 * IN <= width - lampW / 2 && d + 2 * IN <= depth;
  if (wants.low === 'books') {
    const books = propBox('staging-book-stack');
    const vase = propBox('staging-bud-vase');
    if (fits(books.x, books.z)) {
      add(`books-${side}`, 'staging-book-stack', books, [
        innerX(books.x),
        box.top + books.y / 2,
        frontZ(books.z),
      ]);
      add(
        `bud-vase-${side}`,
        'staging-bud-vase',
        vase,
        [innerX(books.x), box.top + books.y + vase.y / 2, frontZ(books.z)],
        { options: { seed: wants.seed ?? (side < 0 ? 5 : 9) } }
      );
    }
  } else if (wants.low === 'plant') {
    const plant = propBox('staging-plant-small');
    if (fits(plant.x, plant.z)) {
      add('plant-small', 'staging-plant-small', plant, [
        innerX(plant.x),
        box.top + plant.y / 2,
        frontZ(plant.z),
      ]);
    }
  }
  return items;
}

/**
 * The rug for a bed, laid crosswise with its far edge 22 in (18-24) past the
 * foot (Atlanta Designer Rugs, Castlery). Larger first: a room that cannot
 * take the bed's own size may take the next one down.
 *
 * @param {number} [pastFoot]  metres the rug runs past the foot
 * @returns {{variant: string|null, dims: object, z: number}[]}  candidates
 *   in the bed frame, the bed's own size first.
 */
function rugCandidatesForBed(size, footZ, pastFoot = 22 * IN) {
  const order = ['king', null, 'twin'];
  const own = rugVariant(size);
  const from = order.indexOf(own);
  return order.slice(from).map((variant) => {
    const dims = propBox('staging-rug', variant);
    return { variant, dims, z: footZ + pastFoot - dims.z / 2 };
  });
}

module.exports = {
  BED_SIZES,
  FRAME_W_IN,
  FRAME_L_IN,
  bedSizeClass,
  artCompanion,
  dressStagedBed,
  stagedArtForBed,
  dressNightstand,
  rugCandidatesForBed,
  propBox,
};
